import { TImageNames } from 'components/image';
import { UNIVERSITY } from 'constants/education';
import { entriesObject, valuesOfObject } from 'services/utils';

import { IEducationLevelBase } from '../education-level';

interface IGetRenderedLevelsPropsParams {
  educationLevels: TEducationLevels;
  isEducationRestricted: boolean;
  isGamifiedEducation: boolean;
}

const imageMap: Record<number, TImageNames> = {
  1: 'primarySchool',
  2: 'secondarySchool',
  3: 'juniorCollage',
  4: 'ite',
  5: 'university',
};

const getEducationLevelProps = (
  level: string,
  levelParams: IStudy
): IEducationLevelBase | undefined => {
  const { preRequisites, achievement, image } = levelParams;

  if (preRequisites && achievement) {
    return {
      prerequisites: preRequisites,
      image: image ? imageMap[image] : 'primarySchool',
      level,
    };
  }
};

const getUniversityPrerequisites = (
  educationLevels: TEducationLevels,
  /** If there is gamified education, University level is available by default */
  isGamifiedEducation: boolean
) => {
  if (isGamifiedEducation) {
    return [];
  }

  // If not, the availability of the University level is determined by prerequisites
  const params = valuesOfObject(educationLevels)
    .filter((levelParams) => levelParams.isUniversity)
    .sort((first, second) => first.order - second.order)[0] as
    | IStudy
    | undefined;

  return params?.preRequisites || [];
};

export const getUniversityLevel = (
  educationLevels: TEducationLevels,
  isGamifiedEducation: boolean
) =>
  ({
    level: UNIVERSITY,
    image: 'university',
    prerequisites: getUniversityPrerequisites(
      educationLevels,
      isGamifiedEducation
    ),
  }) as IEducationLevelBase;

/** Get card component props for each to-render education level */
export const getRenderedLevelsProps = (
  params: IGetRenderedLevelsPropsParams
) => {
  const { educationLevels, isEducationRestricted, isGamifiedEducation } =
    params;

  let levelProps: (IEducationLevelBase | undefined)[] = [];

  // If education is not restricted, render all levels
  if (!isEducationRestricted) {
    levelProps = entriesObject(educationLevels)
      .filter(([, levelParams]) => !levelParams.isUniversity)
      .sort((first, second) => first[1].order - second[1].order)
      .map(([level, levelParams]) =>
        getEducationLevelProps(level, levelParams)
      );
  }

  // Add university level (common card for  levels: Bachelor's, Master's, PhD)
  const universityProps = getUniversityLevel(
    educationLevels,
    isGamifiedEducation
  );

  levelProps.push(universityProps);

  return levelProps;
};
